import { useCallback, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import type {
  UpdateVisitorCustomFieldConfigStatusMutation,
  UpdateVisitorCustomFieldConfigStatusMutationVariables,
} from 'graphql-query-contracts';
import { updateVisitorCustomFieldConfigStatus } from 'graphql-queries';
import type { CustomFieldDetailModel } from './types';
import {
  NO_ORGANIZATION_KEY,
  runUpdateVisitorCustomFieldStatus,
} from './customFieldsTabModelUtils';

export function useCustomFieldStatusUpdate({
  organizationId,
}: {
  organizationId: string | null;
}) {
  const { t } = useTranslation();
  const [pendingDisableRow, setPendingDisableRow] = useState<CustomFieldDetailModel | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [updateFieldStatus] = useMutation<
    UpdateVisitorCustomFieldConfigStatusMutation,
    UpdateVisitorCustomFieldConfigStatusMutationVariables
  >(updateVisitorCustomFieldConfigStatus, {
    errorPolicy: 'all',
    update(cache, { data }) {
      const updatedConfig = data?.updateVisitorCustomFieldConfigStatus;

      if (!updatedConfig) return;

      cache.modify({
        id: cache.identify(updatedConfig),
        fields: {
          isEnabled: () => updatedConfig.isEnabled,
          updatedAtDatetime: () => updatedConfig.updatedAtDatetime,
        },
      });
    },
  });

  const showToast = useCallback((msg: string) => window.Toast.show(msg), []);

  const executeStatusUpdate = useCallback(
    async (row: CustomFieldDetailModel, isEnabled: boolean): Promise<boolean> => {
      if (isLoading) return false;

      const orgId = row.organizationId || organizationId;

      if (!orgId) {
        showToast(t(NO_ORGANIZATION_KEY));

        return false;
      }

      setIsLoading(true);

      try {
        const success = await runUpdateVisitorCustomFieldStatus({
          updateFieldStatus,
          showToast,
          t,
          orgId,
          id: row.id,
          isEnabled,
        });

        if (success && !isEnabled) {
          setPendingDisableRow(null);
        }

        return success;
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading, organizationId, showToast, t, updateFieldStatus]
  );

  const handleToggleStatus = useCallback(
    (row: CustomFieldDetailModel): Promise<boolean> => {
      if (row.isEnabled) {
        setPendingDisableRow(row);

        return Promise.resolve(false);
      }

      return executeStatusUpdate(row, true);
    },
    [executeStatusUpdate]
  );

  const handleDisableConfirm = useCallback(async (): Promise<void> => {
    const row = pendingDisableRow;

    if (!row) return;

    await executeStatusUpdate(row, false);
  }, [pendingDisableRow, executeStatusUpdate]);

  const handleDisableCancel = useCallback((): void => {
    if (!isLoading) {
      setPendingDisableRow(null);
    }
  }, [isLoading]);

  return {
    handleToggleStatus,
    handleDisableConfirm,
    handleDisableCancel,
    pendingDisableRow,
    isLoading,
  };
}
